import { useState, useRef } from 'react';
import { useClickOutside } from '@/hooks/useClickOutside';
import rootBuildInfoJson from '../../../../buildInfo.json';
import reportingBuildInfoJson from '../../../../modules/reporting/buildInfo.json';
import apexForgeBuildInfoJson from '../../../../modules/apex-forge/buildInfo.json';
import adminBuildInfoJson from '../../../../modules/admin/buildInfo.json';

interface BuildInfo {
    name: string;
    description: string;
    author: { name: string; email: string };
    version: string;
    buildDate: string;
    buildTimestamp: number;
    commitHash?: string;
}

type ModuleViewMode = 'compact' | 'detailed';

const primaryApp = rootBuildInfoJson as BuildInfo;
const moduleApps = [reportingBuildInfoJson, apexForgeBuildInfoJson, adminBuildInfoJson] as BuildInfo[];

const displayName = (name: string): string => name.replace(/^@[^/]+\//, '');

const formatBuildDate = (isoString: string): string =>
    new Date(isoString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
    });

export const VersionInfo = () => {
    const [showDetails, setShowDetails] = useState(false);
    const [showModules, setShowModules] = useState(false);
    const [moduleViewMode, setModuleViewMode] = useState<ModuleViewMode>('compact');
    const containerRef = useRef<HTMLDivElement>(null);

    useClickOutside(
        containerRef,
        () => {
            setShowDetails(false);
            setShowModules(false);
        },
        showDetails,
    );

    const environment = document.head.querySelector('meta[name="environment"]')?.getAttribute('content');

    const renderCompactRow = (app: BuildInfo) => (
        <div
            key={app.name}
            className="flex items-center justify-between border-b border-gray-200 py-1 last:border-0 dark:border-gray-700"
        >
            <h4 className="truncate text-xs leading-tight font-medium text-gray-900 dark:text-white">{app.name}</h4>
            <span className="ml-2 shrink-0 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] leading-none font-semibold text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                v{app.version}
            </span>
        </div>
    );

    const renderAppDetails = (app: BuildInfo) => (
        <div key={app.name} className="border-b border-gray-200 pb-2 last:border-0 last:pb-0 dark:border-gray-700">
            <div className="flex items-start justify-between">
                <div>
                    <h4 className="text-xs leading-tight font-bold text-gray-900 dark:text-white">{app.name}</h4>
                    <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-gray-600 dark:text-gray-400">
                        {app.description}
                    </p>
                </div>
                <span className="ml-2 shrink-0 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] leading-none font-semibold text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                    v{app.version}
                </span>
            </div>
            <div className="mt-1.5 space-y-0.5">
                <div className="grid grid-cols-3 gap-2">
                    <span className="col-span-1 text-[11px] text-gray-500 dark:text-gray-400">Built:</span>
                    <span className="col-span-2 text-[11px] font-medium text-gray-900 dark:text-gray-100">
                        {formatBuildDate(app.buildDate)}
                    </span>
                </div>
                {app.commitHash && (
                    <div className="grid grid-cols-3 gap-2">
                        <span className="col-span-1 text-[11px] text-gray-500 dark:text-gray-400">Commit:</span>
                        <span
                            className="col-span-2 truncate font-mono text-[11px] font-medium text-gray-900 dark:text-gray-100"
                            title={app.commitHash}
                        >
                            {app.commitHash.substring(0, 8)}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="relative z-10" ref={containerRef}>
            <button
                onClick={() => setShowDetails(!showDetails)}
                className="rounded px-2 py-1 text-xs text-gray-300 transition-all duration-200 hover:text-white"
                title="Click for build details"
                aria-expanded={showDetails}
                aria-label={`${displayName(primaryApp.name)} version ${primaryApp.version}. Click for details.`}
            >
                v{primaryApp.version}
            </button>

            {showDetails && (
                <div className="absolute bottom-full left-0 z-50 mb-2 w-80 rounded-lg border border-gray-300 bg-white p-3 text-xs text-gray-800 shadow-xl dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                    <div className="space-y-2.5">
                        {environment && (
                            <div className="flex items-center justify-between border-b border-gray-200 pb-2 dark:border-gray-700">
                                <span className="text-[11px] text-gray-500 dark:text-gray-400">Environment:</span>
                                <span
                                    className={`rounded px-1.5 py-0.5 text-[10px] leading-none font-medium ${
                                        environment === 'production'
                                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                    }`}
                                >
                                    {environment}
                                </span>
                            </div>
                        )}

                        {renderAppDetails(primaryApp)}

                        <div className="relative">
                            <div className="flex items-center justify-between">
                                <button
                                    onClick={() => setShowModules(!showModules)}
                                    className="flex flex-1 items-center justify-between text-[11px] font-semibold text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                                    aria-expanded={showModules}
                                >
                                    <span>Module Details ({moduleApps.length})</span>
                                    <span className="text-sm leading-none">{showModules ? '−' : '+'}</span>
                                </button>
                            </div>

                            {showModules && (
                                <div className="mt-2 space-y-2">
                                    <div className="flex items-center justify-end gap-1 pl-1">
                                        <div className="inline-flex rounded-md border border-gray-200 p-0.5 dark:border-gray-700">
                                            <button
                                                onClick={() => setModuleViewMode('compact')}
                                                className={`rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                                                    moduleViewMode === 'compact'
                                                        ? 'bg-gray-200 text-gray-900 dark:bg-gray-600 dark:text-white'
                                                        : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                                                }`}
                                                aria-pressed={moduleViewMode === 'compact'}
                                            >
                                                Compact
                                            </button>
                                            <button
                                                onClick={() => setModuleViewMode('detailed')}
                                                className={`rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                                                    moduleViewMode === 'detailed'
                                                        ? 'bg-gray-200 text-gray-900 dark:bg-gray-600 dark:text-white'
                                                        : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                                                }`}
                                                aria-pressed={moduleViewMode === 'detailed'}
                                            >
                                                Detailed
                                            </button>
                                        </div>
                                    </div>

                                    <div className="max-h-48 space-y-2 overflow-y-auto pr-1 pl-1">
                                        {moduleApps.map((app) =>
                                            moduleViewMode === 'compact'
                                                ? renderCompactRow(app)
                                                : renderAppDetails(app),
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end pt-0.5">
                            <button
                                onClick={() => {
                                    setShowDetails(false);
                                    setShowModules(false);
                                }}
                                className="px-2 py-1 text-[11px] text-blue-600 transition-colors duration-150 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                                aria-label="Close version details"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
